import React, { useState } from 'react';
import { Box } from '@mui/material';
import {
  MarketShell, useMarketData, AreaChartBox,
} from './_shared';
import { ChartSection, StatCard, formatMW } from '../charts/shared';

// Bottom→top stacking: dispatchable baseload first, renewables on top —
// the OpenElectricity convention. Colours echo charts/shared FUEL_COLORS.
const FUEL_ORDER = [
  ['Black Coal', '#374151'],
  ['Brown Coal', '#78350f'],
  ['Gas', '#ef4444'],
  ['Other', '#6b7280'],
  ['Hydro', '#06b6d4'],
  ['Wind', '#3b82f6'],
  ['Solar', '#f59e0b'],
  ['Rooftop Solar', '#fbbf24'],
];

const WINDOWS = [
  { code: 3, label: '3D' },
  { code: 7, label: '7D' },
  { code: 14, label: '14D' },
  { code: 30, label: '30D' },
];

const fmtTs = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString('en-AU', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
};

export default function MarketGeneration() {
  const [days, setDays] = useState(7);
  const [region, setRegion] = useState('NEM');
  const { data, loading, error } = useMarketData('generation', days);

  let body = null;
  if (data) {
    const rows = data.rows || [];
    const kpis = data.kpis || {};
    const times = [...new Set(rows.map((r) => r.t))].sort();
    const idx = new Map(times.map((t, i) => [t, i]));
    const present = new Set(rows.map((r) => r.fuel));

    const series = FUEL_ORDER
      .filter(([fuel]) => present.has(fuel))
      .map(([fuel, color]) => {
        const arr = new Array(times.length).fill(0);
        rows.filter((r) => r.fuel === fuel).forEach((r) => {
          arr[idx.get(r.t)] = r.mw ?? 0;
        });
        return { data: arr, label: fuel, color, connectNulls: true };
      });

    const x = times.map(fmtTs);

    body = (
      <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
          <StatCard label="Renewable share · last 24h"
            value={kpis.renew_pct_24h == null ? '—' : `${kpis.renew_pct_24h}%`}
            color="#D98A2B" />
          <StatCard label="Avg total generation"
            value={kpis.avg_total_mw == null ? '—' : `${(kpis.avg_total_mw / 1000).toFixed(2)} GW`}
            unit="window mean" />
          <StatCard label="Fuel technologies"
            value={(kpis.fuels || []).length || '—'} unit="series" />
        </Box>

        <ChartSection title="Generation mix by fuel"
          subtitle="Dispatch SCADA + rooftop, 30-min, stacked fleet output (MW) — real AEMO actuals">
          <AreaChartBox x={x} series={series} yFormat={formatMW} height={460} />
        </ChartSection>
      </>
    );
  }

  return (
    <MarketShell
      title="Generation Mix"
      subtitle="NEM electricity generation by fuel technology — real AEMO actuals"
      days={days} setDays={setDays} region={region} setRegion={setRegion}
      windows={WINDOWS} loading={loading} error={error}
    >
      {body}
    </MarketShell>
  );
}
